import { useEffect, useState } from 'react';
import useSWR, { type SWRConfiguration } from 'swr';
import { safeValidateAddress } from '@mls-wallet/shared-types';
import denApi from '@/lib/api/denApi';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';

type DisasterRecoveryAddressActivityResponse = {
  hasBeenUsed: boolean;
};

const ADDRESS_ACTIVITY_URL =
  'admin/organization/disaster-recovery-address-activity';
const ADDRESS_ACTIVITY_DEBOUNCE_MS = 400;

const fetchAddressActivity = async (url: string) =>
  await denApi.get<DisasterRecoveryAddressActivityResponse, void>(url);

/**
 * Checks whether the entered recovery wallet has historical activity on any
 * org-deployed network.
 */
export function useDisasterRecoveryAddressActivity(
  walletAddress: string,
  swrOptions?: SWRConfiguration<DisasterRecoveryAddressActivityResponse>
) {
  const { organization } = useMyOrganization();
  const isAdmin = Boolean(organization?.isAdmin);
  const [debouncedWalletAddress, setDebouncedWalletAddress] =
    useState(walletAddress);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedWalletAddress(walletAddress);
    }, ADDRESS_ACTIVITY_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [walletAddress]);

  const validatedAddress = safeValidateAddress(debouncedWalletAddress);
  const checksummedAddress = validatedAddress.isValid
    ? validatedAddress.checksumAddress
    : null;
  const url =
    isAdmin && checksummedAddress
      ? `${ADDRESS_ACTIVITY_URL}?address=${encodeURIComponent(checksummedAddress)}`
      : null;

  const { data, error, isLoading, mutate } =
    useSWR<DisasterRecoveryAddressActivityResponse>(
      url,
      url ? fetchAddressActivity : null,
      {
        revalidateOnFocus: false,
        ...(swrOptions ?? {}),
      }
    );

  return {
    hasBeenUsed: data?.hasBeenUsed ?? false,
    isUsageUnknown: Boolean(url) && Boolean(error),
    error,
    isLoading: Boolean(url) && isLoading,
    refresh: mutate,
  };
}
